import { useCallback, useId, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { AlertCircle, CloudOff, Download, FileText, UploadCloud, X } from "lucide-react";
import { bytes, extension } from "../lib/format";
import { DUR, EASE_OUT } from "../lib/motion";

function isAccepted(file, accept) {
  if (!accept) return true;
  const allowed = accept.split(",").map((part) => part.trim().toLowerCase());
  const name = file.name.toLowerCase();
  return allowed.some((ext) => name.endsWith(ext));
}

/**
 * File input with drag-and-drop.
 *
 * Drag is an enhancement, never the only route: the native file input stays in
 * the DOM and in the tab order, and its label is the visible target, so
 * keyboard and pointer users get the platform picker. Validation runs on drop
 * or selection and reports the accepted formats inline, next to the field.
 */
function Dropzone({
  label,
  accept,
  hint,
  file,
  onSelect,
  disabled = false,
  progress = null,
  required = false,
  //: Set when an upload cannot be processed at all, e.g. the service is down.
  //: The native picker is replaced by a button so the browser never opens a
  //: file dialog for a file that has nowhere to go.
  blocked = false,
  onBlockedClick,
  //: When set, a download control appears beside the remove button. Used for the
  //: bundled samples, so the document behind a result can be inspected.
  downloadUrl,
}) {
  const inputId = useId();
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const prefersReducedMotion = useReducedMotion();
  const inputRef = useRef(null);
  // dragenter/dragleave fire for descendants too, so nesting is counted.
  const depth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");

  // Memoised so the `take` callback below keeps a stable identity.
  const accepted = useMemo(
    () => (accept ? accept.split(",").map((p) => p.trim().toUpperCase().replace(".", "")) : []),
    [accept]
  );

  const take = useCallback(
    (candidate) => {
      if (!candidate) return;
      if (!isAccepted(candidate, accept)) {
        setError(`“${candidate.name}” is not a supported format. Choose a ${accepted.join(" or ")} file.`);
        return;
      }
      setError("");
      onSelect(candidate);
    },
    [accept, accepted, onSelect]
  );

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      depth.current = 0;
      setDragging(false);
      if (disabled) return;
      // A dropped file is the same intent as a picked one, so it gets the same
      // explanation rather than being silently ignored.
      if (blocked) {
        onBlockedClick?.();
        return;
      }
      take(event.dataTransfer?.files?.[0]);
    },
    [disabled, blocked, onBlockedClick, take]
  );

  const onDragEnter = useCallback(
    (event) => {
      event.preventDefault();
      if (disabled || blocked) return;
      depth.current += 1;
      setDragging(true);
    },
    [disabled, blocked]
  );

  const onDragLeave = useCallback((event) => {
    event.preventDefault();
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDragging(false);
  }, []);

  const clear = useCallback(() => {
    setError("");
    onSelect(null);
    if (inputRef.current) inputRef.current.value = "";
  }, [onSelect]);

  const enter = prefersReducedMotion
    ? { duration: 0 }
    : { duration: DUR.enter, ease: EASE_OUT };

  return (
    <div className="field">
      <label className="label" htmlFor={inputId}>
        {label}
        {required && (
          <span className="accent" aria-hidden="true">
            *
          </span>
        )}
        {required && <span className="sr-only">(required)</span>}
      </label>

      <div
        className="drop"
        data-cursor="drop"
        data-dragging={dragging}
        data-filled={Boolean(file)}
        data-invalid={Boolean(error)}
        data-blocked={blocked && !file}
        onDrop={onDrop}
        onDragOver={(event) => event.preventDefault()}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          className="drop__file"
          accept={accept}
          disabled={disabled}
          aria-describedby={error ? `${hintId} ${errorId}` : hintId}
          onChange={(event) => take(event.target.files?.[0])}
        />
        <span className="drop__laser" aria-hidden="true" />

        {blocked && !file ? (
          <button
            type="button"
            className="drop__hit drop__hit--blocked"
            onClick={onBlockedClick}
            aria-describedby={hintId}
          >
            <span className="stack stack-3">
              <span className="drop__icon" aria-hidden="true">
                <CloudOff size={19} />
              </span>
              <span className="drop__title">Uploads are unavailable</span>
              <span className="drop__meta">The service is offline. Tap to see options.</span>
            </span>
          </button>
        ) : (
        <label className="drop__hit" htmlFor={inputId}>
          <AnimatePresence mode="wait" initial={false}>
            {file ? (
              <motion.span
                key="filled"
                className="filecard"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={enter}
              >
                <span className="filecard__glyph" aria-hidden="true">
                  {extension(file.name)}
                </span>
                <span className="grow">
                  <span className="filecard__name">{file.name}</span>
                  <span className="filecard__meta">
                    {bytes(file.size)} · click to replace
                  </span>
                </span>
              </motion.span>
            ) : (
              <motion.span
                key="empty"
                className="stack stack-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={enter}
              >
                <span className="drop__icon" aria-hidden="true">
                  {dragging ? <FileText size={19} /> : <UploadCloud size={19} />}
                </span>
                <span className="drop__title">
                  {dragging ? "Release to attach" : "Drop a file or browse"}
                </span>
                <span className="drop__meta">{accepted.join(" · ")}</span>
              </motion.span>
            )}
          </AnimatePresence>
        </label>
        )}

        {file && !disabled && (
          <div className="drop__actions">
            {downloadUrl && (
              <a
                className="drop__action"
                href={downloadUrl}
                download={file.name}
                title={`Download ${file.name}`}
                aria-label={`Download ${file.name}`}
              >
                <Download size={14} aria-hidden="true" />
              </a>
            )}
            <button
              type="button"
              className="drop__action"
              onClick={clear}
              aria-label={`Remove ${file.name}`}
            >
              <X size={15} aria-hidden="true" />
            </button>
          </div>
        )}

        {progress !== null && progress < 100 && (
          <span className="drop__progress" aria-hidden="true">
            <motion.span
              className="drop__progress-fill"
              style={{ scaleX: progress / 100 }}
              transition={{ duration: 0.2, ease: EASE_OUT }}
            />
          </span>
        )}
      </div>

      <p id={hintId} className="hint">
        {hint}
      </p>

      {/* role="alert" announces validation failures without moving focus. */}
      {error && (
        <p id={errorId} className="drop__error" role="alert">
          <AlertCircle size={13} aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}

export default Dropzone;
